import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import useActivityLog from '../hooks/useActivityLog';
import './ActivityFeed.css';

/**
 * Widget de actividad reciente - Se muestra en el header
 * Similar a las notificaciones de GitHub
 */
const ActivityFeed = () => {
  const [isOpen, setIsOpen] = useState(false);
  const panelRef = useRef(null);
  const { 
    getRecentActivity, 
    getUnseenCount, 
    markAsSeen,
    getStats 
  } = useActivityLog();

  const recentActivities = getRecentActivity(10);
  const unseenCount = getUnseenCount();
  const stats = getStats();

  // Cerrar al hacer click fuera
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (panelRef.current && !panelRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      markAsSeen(); // Marcar como visto al abrir
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, markAsSeen]);

  // Formatear tiempo relativo
  const formatTimeAgo = (timestamp) => {
    const now = new Date();
    const then = new Date(timestamp);
    const diffMs = now - then;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Ahora mismo';
    if (diffMins < 60) return `Hace ${diffMins} min`;
    if (diffHours < 24) return `Hace ${diffHours}h`;
    if (diffDays === 1) return 'Ayer';
    if (diffDays < 7) return `Hace ${diffDays} días`;
    return then.toLocaleDateString('es-ES', { day: 'numeric', month: 'short' });
  };

  // Obtener icono según tipo de actividad
  const getActivityIcon = (activity) => {
    switch (activity.type) {
      case 'checklist_item':
        return activity.action === 'completed' ? '✅' : '⬜';
      case 'status_change':
        const statusIcons = {
          pending: '📦',
          progress: '🔄',
          testing: '🧪',
          completed: '✅'
        };
        return statusIcons[activity.details?.to] || '🔄';
      case 'observation':
        return '💬';
      case 'timer':
        return '⏱️';
      default:
        return '📋';
    }
  };

  // Obtener texto descriptivo de la actividad
  const getActivityText = (activity) => {
    switch (activity.type) {
      case 'checklist_item':
        const action = activity.action === 'completed' ? 'verificado' : 'desmarcado';
        return (
          <>
            <span className="activity-highlight">"{activity.details?.itemText}"</span>
            {' '}{action}
          </>
        );
      case 'status_change':
        const statusNames = {
          pending: 'Pendiente',
          progress: 'En Desarrollo',
          testing: 'Testing',
          completed: 'Completado'
        };
        return (
          <>
            Cambió a <span className="activity-highlight">{statusNames[activity.details?.to]}</span>
          </>
        );
      case 'observation':
        return (
          <>
            Observación {activity.action === 'added' ? 'añadida' : 'editada'}
          </>
        );
      case 'timer':
        return (
          <>
            Timer {activity.action === 'started' ? 'iniciado' : activity.action === 'paused' ? 'pausado' : 'detenido'}
          </>
        );
      default:
        return 'Actividad registrada';
    }
  };

  // Color del indicador según tipo
  const getActivityColor = (activity) => {
    if (activity.type === 'checklist_item' && activity.action === 'completed') return 'green';
    if (activity.type === 'status_change') {
      if (activity.details?.to === 'completed') return 'green';
      if (activity.details?.to === 'testing') return 'yellow';
      if (activity.details?.to === 'progress') return 'blue';
    }
    if (activity.type === 'observation') return 'purple';
    return 'gray';
  };

  return (
    <div className="activity-feed-container" ref={panelRef}>
      <button 
        className={`activity-feed-trigger ${isOpen ? 'active' : ''}`}
        onClick={() => setIsOpen(!isOpen)}
        title="Actividad reciente"
      >
        <span className="activity-icon">📜</span>
        {unseenCount > 0 && (
          <span className="activity-badge">
            {unseenCount > 99 ? '99+' : unseenCount}
          </span>
        )}
      </button>

      {isOpen && (
        <div className="activity-feed-panel">
          <div className="activity-feed-header">
            <h3>Actividad Reciente</h3>
            <div className="activity-stats-mini">
              <span title="Hoy">📅 {stats.today.total}</span>
              <span title="Esta semana">📊 {stats.week.total}</span>
            </div>
          </div>

          <div className="activity-feed-list">
            {recentActivities.length === 0 ? (
              <div className="activity-empty">
                <span className="activity-empty-icon">📭</span>
                <p>No hay actividad registrada</p>
                <p className="activity-empty-hint">
                  Marca items del checklist para comenzar
                </p>
              </div>
            ) : (
              recentActivities.map(activity => (
                <div 
                  key={activity.id} 
                  className={`activity-feed-item color-${getActivityColor(activity)}`}
                >
                  <div className="activity-item-indicator"></div>
                  <div className="activity-item-content">
                    <div className="activity-item-header">
                      <span className="activity-item-icon">{getActivityIcon(activity)}</span>
                      <span className="activity-item-time">{formatTimeAgo(activity.timestamp)}</span>
                    </div>
                    <div className="activity-item-text">
                      {getActivityText(activity)}
                    </div>
                    <div className="activity-item-task">
                      en {activity.taskName}
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>

          <div className="activity-feed-footer">
            <Link to="/activity" className="activity-view-all" onClick={() => setIsOpen(false)}>
              Ver historial completo →
            </Link>
          </div>
        </div>
      )}
    </div>
  );
};

export default ActivityFeed;
